import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { PaymentProcessingModal } from '@/components/ui/payment-processing-modal';
import { Confetti } from '@/components/ui/confetti';
import { usePaymentFlow } from '@/hooks/usePaymentFlow';
import { 
  History, 
  CreditCard, 
  RefreshCw, 
  ExternalLink,
  Calendar,
  DollarSign,
  Package,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiManager } from '../../services/implementations/APIManager';
import { toast } from 'sonner';
import type { PaymentHistoryResponse } from '@/types/payment';

interface PaymentHistoryItem {
  id: string;
  order_id: string;
  description: string;
  amount: number;
  currency: string;
  currency_symbol: string;
  status: 'completed' | 'pending' | 'failed' | 'expired' | 'refunded';
  payment_method: 'alipay' | 'polar' | 'wechat';
  date: string;
  metadata?: string; // QR code URL or payment URL
  type: 'subscription' | 'token';
}

export const OrderHistoryPage: React.FC = () => {
  // State
  const [orders, setOrders] = useState<PaymentHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  
  // Payment continuation state
  const [selectedOrder, setSelectedOrder] = useState<PaymentHistoryItem | null>(null);

  // Use shared payment flow hook
  const paymentFlow = usePaymentFlow({
    onPaymentSuccess: () => {
      loadOrders(page); // Reload current page
    },
  });
  
  // Load orders
  const loadOrders = async (pageNum = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const response: PaymentHistoryResponse = await apiManager.getPaymentHistory(pageNum, 10);
      
      // Transform API response to our format
      const transformedOrders: PaymentHistoryItem[] = response.data.map(item => ({
        id: item.id,
        order_id: item.order_id,
        description: item.description,
        amount: item.amount,
        currency: item.currency || 'CNY',
        currency_symbol: item.currency_symbol || '¥',
        status: item.status as PaymentHistoryItem['status'],
        payment_method: item.payment_method as PaymentHistoryItem['payment_method'],
        date: item.date,
        metadata: item.metadata,
        type: item.product_type === 'token_pack' ? 'token' : 'subscription'
      }));
      
      setOrders(transformedOrders);
      setTotalOrders(response.totalCount);
      setTotalPages(Math.ceil(response.totalCount / 10));
      setPage(pageNum);
      
    } catch (err) {
      console.error('Failed to load order history:', err);
      setError('Failed to load order history');
      toast.error('Failed to load order history');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadOrders();
  }, []);
  
  // Handle continue payment
  const handleContinuePayment = (order: PaymentHistoryItem) => {
    setSelectedOrder(order);

    if (order.metadata) {
      paymentFlow.startPaymentFlow(order.payment_method as 'alipay' | 'polar', {
        orderId: order.order_id,
        paymentUrl: order.metadata
      });
    } else {
      toast.error('Payment information not available');
    }
  };
  

  
  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  
  // Get status badge
  const getStatusBadge = (status: PaymentHistoryItem['status']) => {
    const statusConfig = {
      completed: { variant: 'default' as const, icon: CheckCircle, color: 'text-green-600', label: 'Completed' },
      pending: { variant: 'secondary' as const, icon: Clock, color: 'text-yellow-600', label: 'Pending' },
      failed: { variant: 'destructive' as const, icon: XCircle, color: 'text-red-600', label: 'Failed' },
      expired: { variant: 'destructive' as const, icon: AlertTriangle, color: 'text-orange-600', label: 'Expired' },
      refunded: { variant: 'outline' as const, icon: RefreshCw, color: 'text-blue-600', label: 'Refunded' },
    };
    
    const config = statusConfig[status];
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };
  
  // Get payment method badge
  const getPaymentMethodBadge = (method: PaymentHistoryItem['payment_method']) => {
    const methodConfig = {
      alipay: { color: 'bg-blue-100 text-blue-800', label: 'Alipay' },
      polar: { color: 'bg-purple-100 text-purple-800', label: 'Polar.sh' },
      wechat: { color: 'bg-green-100 text-green-800', label: 'WeChat' },
    };
    
    const config = methodConfig[method];
    
    return (
      <Badge className={config.color}>
        {config.label}
      </Badge>
    );
  };
  
  // Render loading state
  const renderLoading = () => (
    <div className="space-y-4">
      {Array.from({ length: 5 }).map((_, index) => (
        <Card key={index}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="space-y-2 flex-1">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-6 w-16" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
  
  // Render error state
  const renderError = () => (
    <Alert className="bg-red-50 border-red-200">
      <XCircle className="w-4 h-4 text-red-600" />
      <AlertDescription className="text-red-800">
        {error}
      </AlertDescription>
    </Alert>
  );
  
  // Render empty state
  const renderEmpty = () => (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-12">
        <Package className="w-16 h-16 text-gray-400 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Orders Found</h3>
        <p className="text-gray-600 text-center">
          You haven't made any purchases yet. Visit our plans page to get started!
        </p>
      </CardContent>
    </Card>
  );
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 dark:from-slate-900 dark:via-blue-900/20 dark:to-purple-900/20">
      <div className="container mx-auto px-6 py-16">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-2xl bg-gradient-to-r from-slate-500 to-gray-600 shadow-lg">
              <History className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-slate-600 via-gray-600 to-slate-800 bg-clip-text text-transparent">
              Order History
            </h1>
          </div>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            View and manage your purchase history and pending payments
          </p>
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-blue-100 dark:bg-blue-900/20">
                  <Package className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Orders</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalOrders}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-green-100 dark:bg-green-900/20">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Completed</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {orders.filter(o => o.status === 'completed').length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-yellow-100 dark:bg-yellow-900/20">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Pending</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {orders.filter(o => o.status === 'pending').length}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Refresh Button */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Recent Orders</h2>
          <Button 
            variant="outline" 
            onClick={() => loadOrders(page)}
            disabled={loading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
            Refresh
          </Button>
        </div>
        
        {/* Orders List */}
        {loading ? (
          renderLoading()
        ) : error ? (
          renderError()
        ) : orders.length === 0 ? (
          renderEmpty()
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <Card key={order.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg text-gray-900 dark:text-white">
                          {order.description}
                        </h3>
                        {getStatusBadge(order.status)}
                        {getPaymentMethodBadge(order.payment_method)}
                      </div>
                      
                      <div className="flex items-center gap-6 text-sm text-gray-600 dark:text-gray-400">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {formatDate(order.date)}
                        </div>
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4" />
                          {order.currency_symbol}{order.amount.toFixed(2)} {order.currency}
                        </div>
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-4 h-4" />
                          Order #{order.order_id}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {order.status === 'pending' && order.metadata && (
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => handleContinuePayment(order)}
                          className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                        >
                          {order.payment_method === 'polar' ? (
                            <>
                              <ExternalLink className="w-4 h-4 mr-2" />
                              Continue Payment
                            </>
                          ) : (
                            <>
                              <CreditCard className="w-4 h-4 mr-2" />
                              Pay Now
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            <Button
              variant="outline"
              onClick={() => loadOrders(page - 1)}
              disabled={page <= 1 || loading}
            >
              Previous
            </Button>
            
            <div className="flex items-center gap-2">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = Math.max(1, Math.min(totalPages - 4, page - 2)) + i;
                return (
                  <Button
                    key={pageNum}
                    variant={pageNum === page ? "default" : "outline"}
                    size="sm"
                    onClick={() => loadOrders(pageNum)}
                    disabled={loading}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            
            <Button
              variant="outline"
              onClick={() => loadOrders(page + 1)}
              disabled={page >= totalPages || loading}
            >
              Next
            </Button>
          </div>
        )}
        
        {/* Modals */}
        <PaymentProcessingModal
          isOpen={paymentFlow.isPaymentModalVisible}
          onClose={() => {
            paymentFlow.resetPayment();
            setSelectedOrder(null);
          }}
          paymentProvider={(selectedOrder?.payment_method as 'alipay' | 'polar') || 'alipay'}
          paymentUrl={paymentFlow.paymentUrl}
          orderId={paymentFlow.orderId}
          onCheckStatus={paymentFlow.pollPaymentStatus}
          isPolling={paymentFlow.polling}
          paymentStatus={paymentFlow.paymentStatus}
        />

        {/* Confetti Animation */}
        <Confetti
          show={paymentFlow.showConfetti}
          onComplete={() => paymentFlow.setShowConfetti(false)}
        />
      </div>
    </div>
  );
}; 